import { useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import { Zap, ZapOff } from 'lucide-react'
import { ButtonWithOverlay } from '@components/buttons/buttonWithOverlay'
import { Select } from '@components/formFields/select'
import { Button } from '@components/v2/button'
import { templateContext } from '@contexts'
import { useOverlay } from '@global/hooks/useOverlay'
import { LIST_TYPES, TListTypes } from '@global/types'
import { reducer, IState, ACTION_TYPE } from './reducer'
import { useSavePowered } from './useSavePowered'
import { useTemplateColumnsQuery } from './useTemplateColumnsQuery'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

const ModalDescription = () => (
  <div className="font-sans w-1/2 pr-4">
    <h1 className="font-semibold text-xl mb-2">{t`Power with dataset`}</h1>
    <p className="mb-4">
      <Trans>This list management view will be a read-only preview of the dataset.</Trans>
    </p>

    <h2 className="text-xl mb-2">{t`Key features`}</h2>
    <ul className="list-disc pl-5">
      <li>
        <Trans>Visit the dataset directly to manage its values.</Trans>
      </li>
      <li>
        <Trans>Values from the selected dataset will populate this list automatically.</Trans>
      </li>
    </ul>
  </div>
)

interface Props {
  poweredTemplateId: string | null
  listType: TListTypes
  listId: number
  poweredTemplateFieldId: number | string | null
}

/**
 * The powered list modal that controls the powering of a list through template and template column selection.
 */
export const PoweredList = ({
  listId,
  poweredTemplateFieldId,
  listType,
  poweredTemplateId = null,
}: Props) => {
  const initialReducerState: IState = {
    selected: {
      templateId: poweredTemplateId,
      columnId: poweredTemplateFieldId,
    },
    templateOptions: [],
    columnOptions: [],
  }
  const [state, dispatch] = useReducer(reducer, initialReducerState)
  const {
    selected: { templateId: selectedTemplateId, columnId: selectedColumnId },
  } = state

  const { byId: templatesById, fetchTemplates } = useContext(templateContext)
  const isSingleColumn = LIST_TYPES.dropDown === listType

  // FIXME: templatesById contains everything we need except for templateFields which are the columns.
  const {
    data: templateColumns,
    isLoading,
    refetchWithTemplateID,
  } = useTemplateColumnsQuery({
    dispatch,
    selectedTemplateId,
  })

  // Create options for the select template column dropdown, and its selected option.
  // Create select template dropdown options.
  const templateFieldOptions =
    templateColumns?.template_fields?.map(tf => ({ name: tf.field.name, id: tf.id })) ?? []
  const selectedTemplateColumn = selectedColumnId
    ? (templateFieldOptions?.find(tfo => tfo.id === selectedColumnId) ?? null)
    : null
  const templateOptions = useMemo(
    () =>
      Object.values(templatesById)
        .filter(template => !!template.rollup_dataset_id)
        .map(template => ({
          name: template.name,
          id: parseInt(template.id, 10),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)) ?? [],
    [templatesById],
  )

  /**
   * Clear the selected template and column values.
   * - If action is to cancel, reset the values back to original.
   * - If action is to unpower, TODO: Implement
   * - Default behavior is to clear the selected values.
   */
  const onClearSelectTemplate = useCallback(
    (identifier = 'clear') => {
      const columnId = selectedTemplateColumn?.id ?? null
      const templateId = selectedTemplateId ?? poweredTemplateId

      // Reset any cleared values back to original.
      if (identifier === 'cancel') {
        if (poweredTemplateId) {
          void refetchWithTemplateID(poweredTemplateId)
        }
        dispatch({ type: ACTION_TYPE.setSelections, payload: { templateId, columnId } })
        return
      }

      if (identifier === 'unpower') return

      dispatch({ type: ACTION_TYPE.clearSelected })
    },
    [selectedTemplateId, selectedTemplateColumn],
  )

  /**
   * Clear the selected column value.
   * - If modal triggers a cancel
   * @param {string} identifier - The identifier for the action to take, default is to clear the selected column.
   */
  const onClearSelectColumn = useCallback(
    identifier => {
      // Reset any cleared values back to original.
      if (identifier === 'cancel') {
        dispatch({ type: ACTION_TYPE.setColumnSelection, payload: poweredTemplateFieldId })
        return
      }

      // Normal clear value that does not reset to original (powered field id).
      dispatch({ type: ACTION_TYPE.setColumnSelection, payload: null })
    },
    [poweredTemplateFieldId],
  )

  // Overlay hook for opening and closing the modal.
  // - onCloseCallback is used to clear the selected template and column values.
  const { anchorEl, onClick, onClose, open, id } = useOverlay({
    onCloseCallback: identifier => {
      onClearSelectTemplate(identifier)
      onClearSelectColumn(identifier)
    },
  })

  // Hook to manage the powered list being set in the UI, by mutating the query and list
  // on the page.
  const { setPoweredList, unPowerList, isSettingPoweredList } = useSavePowered({
    templateColumns,
    selectedTemplateID: selectedTemplateId,
    listType,
    selectedTemplateColumn,
    afterSetPoweredListCallback: () => {
      onClose(null, 'none') // Close overlay once powered list is setup.
    },
  })

  // When a template is selected, fetch the columns for that template.
  // - Update the selected template value.
  const onSelectTemplate = useCallback(
    (templateId: number | string) => {
      if (isSingleColumn) {
        void refetchWithTemplateID(String(templateId))
        return
      }

      dispatch({ type: ACTION_TYPE.setTemplateSelection, payload: String(id) })
    },
    [isSingleColumn],
  )

  const onSelectColumn = useCallback((columnId: number | string) => {
    dispatch({ type: ACTION_TYPE.setColumnSelection, payload: columnId })
  }, [])

  const onSetPoweredList = useCallback(() => {
    setPoweredList(String(listId))
  }, [listId, setPoweredList])

  useEffect(() => {
    void fetchTemplates()
  }, [fetchTemplates])

  // Trigger template selection when list is powered by template.
  useEffect(() => {
    if (!poweredTemplateId) return

    dispatch({ type: ACTION_TYPE.setTemplateSelection, payload: poweredTemplateId })
  }, [poweredTemplateId])

  function handleCancel(e) {
    onClose(e, 'cancel')
  }

  function handleUnpowering(e) {
    onClose(e, 'unpower')

    unPowerList(String(listId))
  }

  const isNewlyPowered = poweredTemplateId !== selectedTemplateId
  const isConfirmDisabled = isSingleColumn
    ? !isNewlyPowered || !selectedTemplateId || !selectedColumnId
    : !isNewlyPowered || !selectedTemplateId

  return (
    <ButtonWithOverlay>
      <ButtonWithOverlay.Button
        v2Enabled={true}
        popoverId="powered-list"
        onClick={onClick}
        className={open ? 'open' : ''}
      >
        <Zap color="hsla(194, 47%, 20%, 1)" size={18} />
        {t`Power with dataset`}
      </ButtonWithOverlay.Button>
      <ButtonWithOverlay.Overlay
        popoverProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
          open,
          onClose: () => {
            onClose(null, 'cancel')
          },
          anchorEl,
          id,
        }}
        sx={{
          '.MuiPaper-root': {
            width: '100%',
            maxWidth: '640px',
            marginTop: '40px',
          },
        }}
      >
        <section className="flex gap-4 p-9 border-l border-r border-t rounded-t-[8px] border-cv-blue-200">
          <ModalDescription />

          <div className="flex flex-col gap-2 w-1/2 font-sans">
            <h2 className="text-[14px]">{t`Dataset`}</h2>
            <Select
              multi={false}
              name={t`Choose a Template`}
              popoverId="select-template"
              data={templateOptions}
              selectedItems={selectedTemplateId ? [+selectedTemplateId] : []}
              onClearCallback={onClearSelectTemplate}
              onSelectCallback={onSelectTemplate}
            >
              <Select.VirtualList>
                {(index, template) => (
                  <Select.Option key={template.id} id={template.id} index={index}>
                    {template.name}
                  </Select.Option>
                )}
              </Select.VirtualList>
            </Select>
            <div className="text-sm text-cv-blue-500 mb-3">
              <Trans>The dataset that will power this list.</Trans>
            </div>

            {isSingleColumn ? (
              <>
                <Select
                  multi={false}
                  name={t`Select Column`}
                  popoverId="select-column"
                  data={templateFieldOptions}
                  isDisabled={!templateFieldOptions?.length}
                  isLoading={isLoading}
                  selectedItems={selectedTemplateColumn?.id ? [selectedTemplateColumn?.id] : []}
                  onClearCallback={onClearSelectColumn}
                  onSelectCallback={onSelectColumn}
                >
                  <Select.VirtualList>
                    {(index, column) => (
                      <Select.Option key={column.id} id={column.id} index={index}>
                        {column.name}
                      </Select.Option>
                    )}
                  </Select.VirtualList>
                </Select>
                <div className="text-sm text-cv-blue-500">
                  <Trans>Describe the purpose of selecting a column.</Trans>
                </div>
              </>
            ) : null}
          </div>
        </section>

        <footer className="flex items-center bg-cv-blue-100 border border-cv-blue-200 py-3 px-9 rounded-b-[8px] gap-4">
          <Button
            isLoading={isSettingPoweredList}
            variant="dark"
            onClick={onSetPoweredList}
            disabled={isConfirmDisabled}
          >
            <Trans>Confirm</Trans>
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            <Trans>Cancel</Trans>
          </Button>
          {poweredTemplateId ? (
            <Button variant="warn" onClick={handleUnpowering}>
              <ZapOff size={18} strokeWidth={1.5} />
              <Trans>Un-power list</Trans>
            </Button>
          ) : null}
        </footer>
      </ButtonWithOverlay.Overlay>
    </ButtonWithOverlay>
  )
}
